/**
 * courseNo 与指标名称映射配置
 * 根据路由上的 courseNo 匹配对应的指标配置
 */

export interface TableColumn {
    key: string           // 字段key
    title: string         // 列标题
    width?: number | string // 列宽度
    color?: string        // 文字颜色
    fixed?: 'left' | 'right' | false  // 固定列
    align?: 'left' | 'center' | 'right' // 对齐方式
    sortable?: boolean    // 是否可排序
    sortField?: string    // 排序字段名（用于接口请求）
    formatter?: string    // 格式化类型: number | percent | amount | text
}

export interface CourseConfig {
    courseNo: string
    courseName: string
    tableColumns: TableColumn[]
    requestApi: string           // 股池数据请求API
    tradingDaysApi?: string      // 交易日请求API（可选，默认使用通用的）
    wsListKey?: string           // WebSocket listKey
    sortFieldMap?: Record<string, string>  // 排序字段映射
    // 新增配置项
    showFilter?: boolean           // 是否显示筛选按钮，默认true
    showStockCount?: boolean       // 是否显示股票数量，默认false
    wsSubscribeFields?: string[]   // WebSocket订阅字段列表
    // 筹码大师新增配置项
    showStockSearch?: boolean      // 是否显示股票搜索框，默认false
    showSelectedStockInfo?: boolean // 是否显示入选股票信息，默认false
    selectedStockDescription?: string // 入选股票描述文本
    signalTabs?: {                 // 信号标签配置
        latest: string             // 最新信号标签文本
        history: string            // 历史信号标签文本
    }
    signalTypeMap?: {              // 信号类型映射（API参数）
        latest: string             // 最新信号对应的API参数值
        history: string            // 历史信号对应的API参数值
    }
    updateTimeFormat?: 'default' | 'chipMaster'  // 更新时间格式类型
    // 盟主趋势新增配置项
    mainTabs?: {                   // 顶级标签配置（信号池和近30天热门案例）
        signalPool: { key: string; label: string }
        hotCases: { key: string; label: string }
    }
    hotCasesColumns?: TableColumn[]  // 近30天热门案例表格列配置
    hotCasesApi?: string           // 近30天热门案例API
    showOptionalFilter?: boolean   // 是否显示只看自选筛选（仅信号池显示）
    beforeChoseColumns?: TableColumn[]  // 往期入选表格列配置（龙头猎手特有）
}

// 四维猎手配置
const fourHunterConfig: CourseConfig = {
    courseNo: 'e4408ecedc054856946546a185e8c047',
    courseName: '四维猎手',
    requestApi: 'getFourHunterPool',
    tradingDaysApi: 'getTradingDays',
    wsListKey: 'FOUR_HUNTER_LIST',
    sortFieldMap: {
        lastPx: 'LastPx',
        macd: 'Macd',
        amount: 'AmountDif',
        closePx: 'ClosePx',
        selectRate: 'SelectRate',
        highRate: 'HighRate',
        pxChangRate: 'PxChangRate',
        plateName: 'PlateType'
    },
    tableColumns: [
        { key: 'index', title: '', width: 40, fixed: 'left', align: 'center' },
        { key: 'symbol', title: '代码', width: 90, fixed: 'left', align: 'left' },
        { key: 'name', title: '名称', width: 95, fixed: 'left', align: 'left' },
        { key: 'lastPx', title: '最新', width: 80, align: 'right', sortable: true, sortField: 'LastPx', formatter: 'number' },
        { key: 'pxChangRate', title: '涨幅', width: 80, align: 'right', sortable: true, sortField: 'PxChangRate', formatter: 'percent' },
        { key: 'closePx', title: '入选价', width: 80, align: 'right', sortable: true, sortField: 'ClosePx', formatter: 'number' },
        { key: 'selectRate', title: '入选日涨跌幅', width: 100, align: 'right', sortable: true, sortField: 'SelectRate', formatter: 'percent' },
        { key: 'macd', title: '趋势势能', width: 120, align: 'right', sortable: true, sortField: 'Macd' },
        { key: 'amount', title: '资金标尺', width: 120, align: 'right', sortable: true, sortField: 'AmountDif' },
        { key: 'highRate', title: '最高涨幅', width: 100, align: 'right', sortable: true, sortField: 'HighRate', formatter: 'percent' },
        { key: 'plateName', title: '题材追踪', width: 180, align: 'right', sortable: true, sortField: 'PlateType' },
        { key: 'turnover', title: '成交额', width: 120, align: 'right', formatter: 'amount', color: '#19B7E1' },
        { key: 'reason', title: 'AI解读', width: 180, align: 'right', sortable: false }
    ]
}

// 箱体擒龙配置
const boxDragonConfig: CourseConfig = {
    courseNo: '33130259066e4650bd0b7cf6b23d9488',
    courseName: '箱体擒龙',
    requestApi: 'getLadderPoolList',
    tradingDaysApi: 'getTradingDays',
    wsListKey: 'BOX_DRAGON_LIST',
    showFilter: false,
    showStockCount: true,
    wsSubscribeFields: ['lastPx', 'pxChangRate', 'volume', 'businessBalance', 'turnoverRatio'],
    sortFieldMap: {
        lastPx: 'lastPx',
        pxChangRate: 'pxChangeRate',
        selectedPrice: 'selectedPrice',
        highPct: 'highPct',

        businessBalance: 'businessBalance',
        turnoverRatio: 'turnoverRatio'
    },
    tableColumns: [
        { key: 'index', title: '', width: 40, fixed: 'left', align: 'center' },
        { key: 'symbol', title: '代码', width: 90, fixed: 'left', align: 'left' },
        { key: 'name', title: '名称', width: 95, fixed: 'left', align: 'left' },
        { key: 'lastPx', title: '最新', width: 80, align: 'right', sortable: true, sortField: 'lastPx', formatter: 'number' },
        { key: 'pxChangRate', title: '涨幅', width: 80, align: 'right', sortable: true, sortField: 'pxChangeRate', formatter: 'percent' },
        //    d加一个入选价
        { key: 'selectedPrice', title: '入选价', width: 80, align: 'right', sortable: true, sortField: 'selectedPrice', formatter: 'number' },
        { key: 'highPct', title: '最高涨幅', width: 100, align: 'right', sortable: true, sortField: 'highPct', formatter: 'percent' },
        { key: 'volume', title: '总量', width: 100, align: 'right', sortable: false, sortField: 'volume', formatter: 'amount', color: '#959EAD' },
        { key: 'businessBalance', title: '金额', width: 100, align: 'right', sortable: true, sortField: 'businessBalance', formatter: 'amount', color: '#19B7E1' },
        { key: 'turnoverRatio', title: '换手', width: 80, align: 'right', sortable: true, sortField: 'turnoverRatio', formatter: 'percent', color: '#959EAD' }
    ]
}

// 筹码大师配置
const chipMasterConfig: CourseConfig = {
    courseNo: '17d1f0b0296e4b40ada792c7aa1599ec',
    courseName: '筹码大师',
    requestApi: 'getChipMasterPool',
    wsListKey: 'CHIP_MASTER_LIST',
    showFilter: false,
    showStockCount: false,
    showStockSearch: true,
    showSelectedStockInfo: true,
    selectedStockDescription: '精选五个交易日内发生过股价突破获利筹码密集区间临界值的个股，用于捕捉趋势启动初期的机会',
    signalTabs: {
        latest: '上突破密集区',
        history: '强支撑区'
    },
    signalTypeMap: {
        latest: 'upBreak',
        history: 'strongSupport'
    },
    updateTimeFormat: 'chipMaster',
    sortFieldMap: {
        lastPx: 'lastPx',
        pxChangRate: 'pxChangeRate',
        state: 'state',

    },
    tableColumns: [
        { key: 'index', title: '', width: 40, fixed: 'left', align: 'center' },
        { key: 'symbol', title: '代码', width: 90, fixed: 'left', align: 'left' },
        { key: 'name', title: '名称', fixed: 'left', width: 95, align: 'left' },
        { key: 'lastPx', title: '最新', width: 80, align: 'right', sortable: true, sortField: 'lastPx', formatter: 'number' },
        { key: 'pxChangRate', title: '涨幅', width: 80, align: 'right', sortable: true, sortField: 'pxChangeRate', formatter: 'percent' },
        { key: 'state', title: '筹码状态', width: 100, align: 'right', sortable: true, sortField: 'state' },
        { key: 'shape', title: '筹码形态', width: 100, align: 'right', sortable: false },
        { key: 'inPoolTime', title: '入选时间', width: 120, align: 'right', formatter: 'date' }
    ]
}

// 盟主趋势配置
const mengzhuTrendConfig: CourseConfig = {
    courseNo: '399a9139a99a4300a9bf004b9949eb9f',
    courseName: '盟主趋势',
    requestApi: 'getMengzhuTrendPool',
    hotCasesApi: 'getMengzhuTrendCard',
    tradingDaysApi: 'getTradingDays',  // 使用通用交易日API
    wsListKey: 'MENGZHU_TREND_LIST',
    showFilter: false,
    showStockCount: false,
    showStockSearch: false,
    showSelectedStockInfo: false,
    showOptionalFilter: true,
    // 顶级Tab配置
    mainTabs: {
        signalPool: { key: 'signalPool', label: '信号池' },
        hotCases: { key: 'hotCases', label: '近30天热门案例' }
    },
    // 信号池子Tab配置
    signalTabs: {
        latest: '最新信号',
        history: '历史信号'
    },
    updateTimeFormat: 'default',  // 使用默认格式（与四维猎手一致）
    sortFieldMap: {
        lastPx: 'LastPx',
        pxChangRate: 'PxChangeRate',
        buyPx: 'BuyPx',
        maxRate: 'MaxRate',
        cumRate: 'CumRate',
        amount: 'Amount',
        fund: 'Fund',
        sellTime: 'SellTime',
        plateName: 'PlateHot'
    },
    // 信号池表格列配置
    tableColumns: [
        { key: 'index', title: '', width: 40, fixed: 'left', align: 'center' },
        { key: 'symbol', title: '代码', width: 90, fixed: 'left', align: 'left' },
        { key: 'name', title: '名称', fixed: 'left', width: 95, align: 'left' },
        { key: 'lastPx', title: '最新', width: 80, align: 'right', sortable: true, sortField: 'LastPx', formatter: 'number' },
        { key: 'pxChangRate', title: '涨幅', width: 80, align: 'right', sortable: true, sortField: 'PxChangeRate', formatter: 'percent' },
        { key: 'buyPx', title: '入选价', width: 80, align: 'right', sortable: true, sortField: 'BuyPx', formatter: 'number' },
        { key: 'maxRate', title: '最高涨幅', width: 100, align: 'right', sortable: true, sortField: 'MaxRate', formatter: 'percent' },
        { key: 'fund', title: '盟主资金', width: 100, align: 'right', sortable: true, sortField: 'fund' },
        { key: 'amount', title: '成交额', width: 100, align: 'right', sortable: true, sortField: 'Amount', formatter: 'amount', color: '#19B7E1' },
        { key: 'plateName', title: '题材板块', width: 120, align: 'right', sortable: true, sortField: 'PlateName' },
        { key: 'cumRate', title: '累计涨幅', width: 100, align: 'right', sortable: true, sortField: 'CumRate', formatter: 'percent' },
        { key: 'sellTime', title: '离场信号时间', width: 120, align: 'right', sortable: true, sortField: 'SellTime', formatter: 'date' },
        { key: 'reason', title: 'AI解读', width: 180, align: 'right', sortable: false }
    ],
    // 近30天热门案例表格列配置（无排序）
    hotCasesColumns: [
        { key: 'index', title: '', width: 40, fixed: 'left', align: 'center' },
        { key: 'symbol', title: '代码', width: 90, fixed: 'left', align: 'left' },
        { key: 'name', title: '名称', fixed: 'left', width: 95, align: 'left' },
        { key: 'lastPx', title: '最新', width: 80, align: 'right', formatter: 'number' },
        { key: 'pxChangRate', title: '涨幅', width: 80, align: 'right', formatter: 'percent' },
        { key: 'buyPx', title: '入选价', width: 80, align: 'right', formatter: 'number' },
        { key: 'buyTime', title: '入选日期', width: 100, align: 'right', formatter: 'date' },
        { key: 'sellTime', title: '离场日期', width: 100, align: 'right', formatter: 'date' },

        { key: 'cumRate', title: '累计涨幅', width: 100, align: 'right', formatter: 'percent' },
        { key: 'amount', title: '成交额', width: 100, align: 'right', formatter: 'amount', color: '#19B7E1' },
        { key: 'reason', title: 'AI解读', width: 180, align: 'right', sortable: false }
    ]
}

// 龙头信号配置
const dragonSignalConfig: CourseConfig = {
    courseNo: 'b20f2d6e654948ae8c99bf650cbbb0e2',
    courseName: '龙头信号',
    requestApi: 'getDragonSignalPool',
    tradingDaysApi: 'getTradingDays',
    wsListKey: 'DRAGON_SIGNAL_LIST',
    showFilter: false,
    showStockCount: false,
    showOptionalFilter: true,
    // 上方 Tab：龙头冲刺 / 龙头点火
    mainTabs: {
        signalPool: { key: 'dragon_signal_sprint', label: '龙头冲刺' },
        hotCases: { key: 'dragon_signal_ignition', label: '龙头点火' }
    },
    // 下方 Tab：最新信号 / 历史信号 / 回测数据跟踪
    signalTabs: {
        latest: '最新信号',
        history: '历史信号'
    },
    updateTimeFormat: 'default',
    sortFieldMap: {
        lastPx: 'lastPx',
        pxChangRate: 'pxChangeRate',
        highPct: 'highPct',
        stockHot: 'stock_hot'
    },
    tableColumns: [
        { key: 'index', title: '', width: 40, fixed: 'left', align: 'center' },
        { key: 'symbol', title: '代码', width: 90, fixed: 'left', align: 'left' },
        { key: 'name', title: '名称', fixed: 'left', width: 95, align: 'left' },
        { key: 'lastPx', title: '最新', width: 80, align: 'right', sortable: true, sortField: 'lastPx', formatter: 'number' },
        { key: 'pxChangRate', title: '涨幅', width: 80, align: 'right', sortable: true, sortField: 'pxChangeRate', formatter: 'percent' },
        { key: 'selectedPrice', title: '入选价', width: 80, align: 'right', formatter: 'number' },
        { key: 'highPct', title: '最高涨幅', width: 100, align: 'right', sortable: true, sortField: 'highPct', formatter: 'percent' },
        { key: 'stockHot', title: '个股热度', width: 100, align: 'right', sortable: true, sortField: 'stockHot' },
        { key: 'dragonPlateName', title: '所属板块', width: 120, align: 'right' },
        { key: 'amount', title: '成交额', width: 120, align: 'right', formatter: 'amount', color: '#19B7E1' }
    ]
}

// 龙头猎手配置
const dragonHunterConfig: CourseConfig = {
    courseNo: '03fc903a5ac84ff6982e4b83a1f35c61',
    courseName: '龙头猎手',
    requestApi: 'getDragonHunterNewChose',
    wsListKey: 'DRAGON_HUNTER_LIST',
    showFilter: false,
    showStockCount: false,
    showOptionalFilter: false,
    // 上方一级 Tab
    mainTabs: {
        signalPool: { key: 'newChose', label: '最新入选' },
        hotCases: { key: 'dataTrack', label: '数据追踪' }
    },
    sortFieldMap: {
        lastPx: 'lastPx',
        pxChangRate: 'pxChangRate',
        price: 'price',
        symbolHot: 'symbolHot',
        plateHot: 'plateHot',
        amount: 'amount',
        // 往期入选排序字段（key -> 接口参数名）
        day5Rate: 'day5Rate',
        selectType: 'choseType',
        selectTime: 'feed_timestamp',
        hunterSelectType: 'choseType',
        hunterDateTime: 'feed_timestamp'
    },
    // 最新入选表格列
    tableColumns: [
        { key: 'index', title: '', width: 40, fixed: 'left', align: 'center' },
        { key: 'symbol', title: '代码', width: 90, fixed: 'left', align: 'left' },
        { key: 'name', title: '名称', fixed: 'left', width: 95, align: 'left' },
        { key: 'lastPx', title: '最新', width: 80, align: 'right', sortable: true, sortField: 'lastPx', formatter: 'number' },
        { key: 'pxChangRate', title: '涨幅', width: 80, align: 'right', sortable: true, sortField: 'pxChangRate', formatter: 'percent' },
        { key: 'price', title: '入选价', width: 80, align: 'right', sortable: true, sortField: 'price', formatter: 'number' },
        { key: 'symbolHot', title: '个股热度', width: 100, align: 'right', sortable: true, sortField: 'symbolHot' },
        { key: 'plateName', title: '所属板块', width: 120, align: 'right', sortable: true, sortField: 'plateName' },
        { key: 'plateHot', title: '板块热度', width: 100, align: 'right', sortable: true, sortField: 'plateHot' },
        { key: 'amount', title: '成交额', width: 120, align: 'right', sortable: true, sortField: 'amount', formatter: 'amount', color: '#19B7E1' }
    ],
    // 热门案例表格列
    hotCasesColumns: [
        { key: 'index', title: '', width: 40, fixed: 'left', align: 'center' },
        { key: 'symbol', title: '代码', width: 90, fixed: 'left', align: 'left' },
        { key: 'name', title: '名称', fixed: 'left', width: 95, align: 'left' },
        { key: 'price', title: '入选价', width: 80, align: 'right', sortField: 'price', formatter: 'number' },
        { key: 'hunterDateTime', title: '入选时间', width: 110, align: 'right', sortField: 'hunterDateTime' },
        { key: 'day5Rate', title: '5日区间最高涨幅', width: 130, align: 'right',  sortField: 'day5Rate', formatter: 'percent' },
        { key: 'hunterSelectType', title: '入选类型', width: 100, align: 'right', sortField: 'hunterSelectType' },
        { key: 'lastPx', title: '最新', width: 80, align: 'right', sortField: 'lastPx', formatter: 'number' },
        { key: 'pxChangRate', title: '涨幅', width: 80, align: 'right', sortField: 'pxChangRate', formatter: 'percent' },
        { key: 'amount', title: '成交额', width: 120, align: 'right', sortField: 'amount', formatter: 'amount', color: '#19B7E1' }
    ],
    // 往期入选表格列（代码、名称、入选5日最高涨幅、入选价、入选类型、入选时间、最新、涨幅、成交额）
    beforeChoseColumns: [
        { key: 'index', title: '', width: 40, fixed: 'left', align: 'center' },
        { key: 'symbol', title: '代码', width: 90, fixed: 'left', align: 'left' },
        { key: 'name', title: '名称', fixed: 'left', width: 95, align: 'left' },
        { key: 'day5Rate', title: '入选5日最高涨幅', width: 130, align: 'right', sortable: true, sortField: 'day5Rate', formatter: 'percent' },
        { key: 'price', title: '入选价', width: 80, align: 'right', sortable: true, sortField: 'price', formatter: 'number' },
        { key: 'hunterSelectType', title: '入选类型', width: 100, align: 'right', sortable: true, sortField: 'choseType' },
        { key: 'hunterDateTime', title: '入选时间', width: 110, align: 'right', sortable: true, sortField: 'feed_timestamp' },
        { key: 'lastPx', title: '最新', width: 80, align: 'right', formatter: 'number', sortable: true },
        { key: 'pxChangRate', title: '涨幅', width: 80, align: 'right', formatter: 'percent', sortable: true },
        { key: 'amount', title: '成交额', width: 120, align: 'right', formatter: 'amount', color: '#19B7E1', sortable: true }
    ]
}

// 所有课程配置列表
export const courseConfigList: CourseConfig[] = [
    fourHunterConfig,
    boxDragonConfig,
    chipMasterConfig,
    mengzhuTrendConfig,
    dragonSignalConfig,
    dragonHunterConfig
]

// courseNo 映射表
export const courseConfigMap: Record<string, CourseConfig> = {
    'e4408ecedc054856946546a185e8c047': fourHunterConfig,
    '33130259066e4650bd0b7cf6b23d9488': boxDragonConfig,
    '17d1f0b0296e4b40ada792c7aa1599ec': chipMasterConfig,
    '399a9139a99a4300a9bf004b9949eb9f': mengzhuTrendConfig,
    'b20f2d6e654948ae8c99bf650cbbb0e2': dragonSignalConfig,
    '03fc903a5ac84ff6982e4b83a1f35c61': dragonHunterConfig
}/**
 * 根据 courseNo 获取课程配置
 * @param courseNo 课程编号
 * @returns 课程配置，如果未找到则返回默认的四维猎手配置
 */
export function getCourseConfig(courseNo: string): CourseConfig {
    return courseConfigMap[courseNo] || fourHunterConfig
}

/**
 * 根据 courseNo 获取表格列配置
 * @param courseNo 课程编号
 * @returns 表格列配置数组
 */
export function getTableColumns(courseNo: string): TableColumn[] {
    const config = getCourseConfig(courseNo)
    return config.tableColumns
}

/**
 * 根据 courseNo 获取近30天热门案例表格列配置（盟主趋势特有）
 * @param courseNo 课程编号
 * @returns 表格列配置数组
 */
export function getHotCasesColumns(courseNo: string): TableColumn[] {
    const config = getCourseConfig(courseNo)
    return config.hotCasesColumns || config.tableColumns
}

/**
 * 根据 courseNo 获取课程名称
 * @param courseNo 课程编号
 * @returns 课程名称
 */
export function getCourseName(courseNo: string): string {
    const config = getCourseConfig(courseNo)
    return config.courseName
}

/**
 * 根据 courseNo 获取请求API名称
 * @param courseNo 课程编号
 * @returns API名称
 */
export function getRequestApi(courseNo: string): string {
    const config = getCourseConfig(courseNo)
    return config.requestApi
}

/**
 * 根据 courseNo 获取交易日API名称
 * @param courseNo 课程编号
 * @returns 交易日API名称
 */
export function getTradingDaysApi(courseNo: string): string {
    const config = getCourseConfig(courseNo)
    return config.tradingDaysApi || 'getTradingDays'
}

/**
 * 根据 courseNo 获取 WebSocket listKey
 * @param courseNo 课程编号
 * @returns WebSocket listKey
 */
export function getWsListKey(courseNo: string): string {
    const config = getCourseConfig(courseNo)
    return config.wsListKey || 'FOUR_HUNTER_LIST'
}

/**
 * 根据 courseNo 获取排序字段映射
 * @param courseNo 课程编号
 * @returns 排序字段映射
 */
export function getSortFieldMap(courseNo: string): Record<string, string> {
    const config = getCourseConfig(courseNo)
    return config.sortFieldMap || {}
}

export default {
    courseConfigList,
    courseConfigMap,
    getCourseConfig,
    getTableColumns,
    getCourseName,
    getRequestApi,
    getTradingDaysApi,
    getWsListKey,
    getSortFieldMap
}
